import { z } from 'zod';

export const HttpEvidenceMatchOutcomeSchema = z.enum([
  'matched',
  'no_hars',
  'no_match',
  'ambiguous',
  'har_fetch_error',
]);

export const HttpEvidenceMatchRejectionCriterionSchema = z.enum([
  'method',
  'url',
  'status',
  'requestMarker',
  'responseMarker',
]);

export const HttpEvidenceMatchCandidateSummarySchema = z.object({
  method: z.string(),
  url: z.string(),
  status: z.int(),
});

export const HttpEvidenceMatchRejectionDetailSchema = z.object({
  harId: z.string(),
  criterion: HttpEvidenceMatchRejectionCriterionSchema,
  expected: z.string(),
  actual: z.string().optional(),
});

export const HttpEvidenceMatchEventDtoSchema = z.object({
  id: z.string(),
  tenantId: z.string(),
  applicationId: z.string(),
  threadId: z.string(),
  stepId: z.string(),
  toolCallId: z.string(),
  signalId: z.string().nullable(),
  outcome: HttpEvidenceMatchOutcomeSchema,
  method: z.string(),
  urlContains: z.string(),
  status: z.int(),
  requestMarker: z.string(),
  responseMarker: z.string(),
  matchedHarId: z.string().nullable(),
  rejectionCriteria: HttpEvidenceMatchRejectionCriterionSchema.array().default(
    [],
  ),
  rejectionDetails: HttpEvidenceMatchRejectionDetailSchema.array().default([]),
  ambiguousCount: z.int().nonnegative().nullable(),
  candidateSummaries: HttpEvidenceMatchCandidateSummarySchema.array().default(
    [],
  ),
  createdAt: z.iso.datetime(),
});

export const HttpEvidenceMatchOutcomeCountSchema = z.object({
  outcome: HttpEvidenceMatchOutcomeSchema,
  count: z.int().nonnegative(),
});

export const HttpEvidenceMatchRejectionCriterionCountSchema = z.object({
  criterion: HttpEvidenceMatchRejectionCriterionSchema,
  count: z.int().nonnegative(),
});

export const HttpEvidenceMatchDailyOutcomeSchema = z.object({
  date: z.iso.date(),
  matched: z.int().nonnegative(),
  noHars: z.int().nonnegative(),
  noMatch: z.int().nonnegative(),
  ambiguous: z.int().nonnegative(),
  harFetchError: z.int().nonnegative(),
});

export const HttpEvidenceMatchDashboardSchema = z.object({
  summary: z.object({
    invocations: z.int().nonnegative(),
    matchRate: z.number().min(0).max(100),
    noMatchRate: z.number().min(0).max(100),
    ambiguousRate: z.number().min(0).max(100),
    noHarsRate: z.number().min(0).max(100),
    harFetchErrorRate: z.number().min(0).max(100),
    lastEventAt: z.iso.datetime().nullable(),
  }),
  outcomeCounts: HttpEvidenceMatchOutcomeCountSchema.array(),
  rejectionCriterionCounts:
    HttpEvidenceMatchRejectionCriterionCountSchema.array(),
  dailyOutcomes: HttpEvidenceMatchDailyOutcomeSchema.array(),
});

export const HttpEvidenceMatchDateRangeSchema = z
  .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
  })
  .refine(({ from, to }) => new Date(from) <= new Date(to), {
    error: 'from must be before or equal to to',
  });

export const GetHttpEvidenceMatchDashboardQuerySchema =
  HttpEvidenceMatchDateRangeSchema;

export const ListHttpEvidenceMatchEventsQuerySchema =
  HttpEvidenceMatchDateRangeSchema.and(
    z.object({
      outcome: HttpEvidenceMatchOutcomeSchema.optional(),
      tenantId: z.string().optional(),
      applicationId: z.string().optional(),
      cursor: z.string().optional(),
      limit: z.coerce.number().int().min(1).max(100).prefault(25),
    }),
  );

export const ListHttpEvidenceMatchEventsResponseSchema = z.object({
  items: HttpEvidenceMatchEventDtoSchema.array(),
  nextCursor: z.string().nullable(),
});

export type HttpEvidenceMatchOutcome = z.infer<
  typeof HttpEvidenceMatchOutcomeSchema
>;
export type HttpEvidenceMatchRejectionCriterion = z.infer<
  typeof HttpEvidenceMatchRejectionCriterionSchema
>;
export type HttpEvidenceMatchCandidateSummary = z.infer<
  typeof HttpEvidenceMatchCandidateSummarySchema
>;
export type HttpEvidenceMatchRejectionDetail = z.infer<
  typeof HttpEvidenceMatchRejectionDetailSchema
>;
export type HttpEvidenceMatchEventDto = z.infer<
  typeof HttpEvidenceMatchEventDtoSchema
>;
export type HttpEvidenceMatchDashboard = z.infer<
  typeof HttpEvidenceMatchDashboardSchema
>;
export type ListHttpEvidenceMatchEventsResponse = z.infer<
  typeof ListHttpEvidenceMatchEventsResponseSchema
>;
